import React, { useState } from 'react';
import { diff, fmtLong, fmtShort } from './dates.js';

function money(n, currency) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'GBP',
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

function propertyUrl(slug, params) {
  return '/homes/' + encodeURIComponent(slug) + '/?in=' + encodeURIComponent(params.in) +
    '&out=' + encodeURIComponent(params.out) +
    '&a=' + encodeURIComponent(params.a) +
    '&c=' + encodeURIComponent(params.c);
}

/* Plain-English reason for a non-matching home. "Booked" copy is neutral -
   Guesty blocks include owner holds, so never "another guest has it". */
function reason(result, home, params) {
  switch (result.status) {
    case 'capacity':
      return 'Sleeps ' + home.maxGuests + ' - your party is ' + (params.a + params.c) + '.';
    case 'booked':
      return result.overlapFrom && result.overlapTo
        ? fmtShort(result.overlapFrom) + ' – ' + fmtShort(result.overlapTo) + ' is already booked.'
        : 'Those dates are already booked.';
    case 'minimum_stay':
      return 'These dates need a ' + result.minNights + '-night minimum stay - yours is ' +
        params.nights + ' night' + (params.nights === 1 ? '' : 's') + '.';
    case 'checking':
      return 'Checking availability…';
    default:
      return 'We could not check this home just now.';
  }
}

/* Card blurb, clamped to two lines with a Read more toggle when long. */
function Blurb({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const toggleable = text.length > 140;
  return (
    <div className="shs-search-card-blurbwrap">
      <div className={'shs-search-card-blurb' + (toggleable && !expanded ? ' shs-search-card-blurb-clamped' : '')}>
        {text}
      </div>
      {toggleable && (
        <button
          type="button"
          className="shs-search-readmore"
          aria-expanded={expanded}
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

export default function SearchPropertyCard({ result, home, params, suggestions, onPickDates }) {
  const available = result.status === 'available';
  const url = propertyUrl(home.slug, params);

  const photo = (
    <img
      src={home.photo.med || home.photo.src}
      srcSet={home.photo.thumb ? home.photo.thumb + ' 600w, ' + home.photo.med + ' 1200w' : undefined}
      sizes="(max-width: 720px) 92vw, 280px"
      alt={home.photo.alt || home.name}
      loading="lazy"
      decoding="async"
      className="shs-search-card-photo"
    />
  );

  if (available) {
    return (
      <div
        className="shs-search-card shs-search-card-available"
        onClick={(e) => {
          // Whole-card click is a convenience; inner controls keep their jobs.
          if (e.target.closest('button, a')) return;
          window.location.href = url;
        }}
      >
        {photo}
        <div className="shs-search-card-body">
          <div className="shs-search-card-name">{home.name}</div>
          <div className="shs-search-card-meta">{home.summaryLine}</div>
          <Blurb text={home.blurb} />
        </div>
        <div className="shs-search-card-side">
          {result.pending ? (
            <div className="shs-search-card-total-pending" role="status">Confirming price…</div>
          ) : (
            <>
              <div className="shs-search-card-total">{money(result.total, result.currency)}</div>
              <div className="shs-search-card-total-note">
                total for {result.nights} night{result.nights === 1 ? '' : 's'}, all fees included
              </div>
            </>
          )}
          <a href={url} className="shs-search-card-cta">Continue to book →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="shs-search-card shs-search-card-unavailable">
      {photo}
      <div className="shs-search-card-body">
        <div className="shs-search-card-name">{home.name}</div>
        <div className="shs-search-card-meta">{home.summaryLine}</div>
        <Blurb text={home.blurb} />
        <div className="shs-search-card-reason">
          {reason(result, home, params)}
          {suggestions && suggestions.length > 0 && <strong> Try these dates instead:</strong>}
        </div>
        {suggestions && suggestions.length > 0 && (
          <div className="shs-search-card-alts">
            {suggestions.map((s) => {
              const len = diff(s.in, s.out);
              return (
                <button
                  key={s.in + s.out}
                  type="button"
                  className="shs-search-card-alt"
                  aria-label={'Search all homes for ' + fmtLong(s.in) + ' to ' + fmtLong(s.out)}
                  onClick={() => onPickDates?.(s)}
                >
                  {fmtShort(s.in)} – {fmtShort(s.out)}
                  {len !== params.nights && (
                    <span className="shs-search-card-alt-len"> · {len} night{len === 1 ? '' : 's'}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="shs-search-card-side">
        <a href={url} className="shs-search-card-calendar-link">See its calendar →</a>
      </div>
    </div>
  );
}
